/**
 * CodeMirror Extension: Template Syntax Decorations
 *
 * Provides visual highlighting for prompt template syntax:
 * - Parameters {{param-name}} - green (has value) or red (missing value)
 * - Control flow {{if}}, {{else}}, {{endif}}, {{prob}}, {{endprob}} - purple
 *
 * This extension scans the document for {{...}} patterns and applies
 * background color decorations based on parameter availability.
 */

import {
  ViewPlugin,
  Decoration,
  DecorationSet,
  EditorView,
  ViewUpdate,
} from '@codemirror/view';
import { RangeSetBuilder } from '@codemirror/state';

/**
 * Control flow keywords that should be highlighted in purple
 */
const CONTROL_FLOW_KEYWORDS = new Set([
  'if',
  'else',
  'endif',
  'prob',
  'endprob',
]);

/**
 * Color scheme for decorations (MUI-compatible colors)
 * Light mode uses lighter shades, dark mode uses darker shades
 */
const COLORS_LIGHT = {
  parameterAvailable: '#c8e6c9', // MUI green[100] - parameter has value
  parameterMissing: '#ffcdd2', // MUI red[100] - parameter missing
  controlFlow: '#e1bee7', // MUI purple[100] - control flow keywords
};

const COLORS_DARK = {
  parameterAvailable: '#1b5e20', // MUI green[900] - parameter has value
  parameterMissing: '#b71c1c', // MUI red[900] - parameter missing
  controlFlow: '#4a148c', // MUI purple[900] - control flow keywords
};

/**
 * Create decoration marks for different syntax types
 * @param isDark - Whether to use dark mode colors
 */
function createDecorations(isDark: boolean) {
  const COLORS = isDark ? COLORS_DARK : COLORS_LIGHT;

  return {
    parameterAvailable: Decoration.mark({
      class: 'cm-template-param-available',
      attributes: { style: `background-color: ${COLORS.parameterAvailable}` },
    }),
    parameterMissing: Decoration.mark({
      class: 'cm-template-param-missing',
      attributes: { style: `background-color: ${COLORS.parameterMissing}` },
    }),
    controlFlow: Decoration.mark({
      class: 'cm-template-control-flow',
      attributes: { style: `background-color: ${COLORS.controlFlow}` },
    }),
  };
}

/**
 * Regex to match template syntax: {{...}}
 * Matches double curly brackets with content inside
 */
const TEMPLATE_PATTERN = /\{\{([^}]+)\}\}/g;

/**
 * Parse template directive content to extract parameter name or keyword
 * @param content - Raw content between {{ and }}
 * @returns Trimmed content
 */
function parseDirectiveContent(content: string): string {
  return content.trim();
}

/**
 * Check if directive is a control flow keyword
 * @param content - Parsed directive content
 * @returns True if it's a control flow keyword
 */
function isControlFlowKeyword(content: string): boolean {
  const firstToken = content.split(/\s+/)[0]?.toLowerCase() || '';
  return CONTROL_FLOW_KEYWORDS.has(firstToken);
}

/**
 * Check if directive is a JSON snippet (should be treated as literal text)
 * Example: {{"shows": [...]}} or {{"key": "value"}}
 * @param content - Parsed directive content
 * @returns True if it looks like JSON
 */
function isJsonSnippet(content: string): boolean {
  return /"[^"]*"\s*:/.test(content);
}

/**
 * Create template decoration extension
 * @param availableParams - Record of parameter names to their values
 * @param isDark - Whether to use dark mode colors
 * @returns CodeMirror ViewPlugin
 */
export function createTemplateDecorationExtension(
  availableParams: Record<string, string | number | undefined>,
  isDark = false
) {
  const decorations = createDecorations(isDark);

  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = this.buildDecorations(
          view,
          availableParams,
          decorations
        );
      }

      update(update: ViewUpdate) {
        // Rebuild decorations when document changes
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.buildDecorations(
            update.view,
            availableParams,
            decorations
          );
        }
      }

      /**
       * Build decoration set by scanning document for template syntax
       * Note: This method doesn't use 'this' but is required by ViewPlugin architecture
       */
      // eslint-disable-next-line class-methods-use-this
      buildDecorations(
        view: EditorView,
        params: Record<string, string | number | undefined>,
        decs: ReturnType<typeof createDecorations>
      ): DecorationSet {
        const builder = new RangeSetBuilder<Decoration>();
        const { doc } = view.state;

        // Iterate through visible lines
        for (let i = 1; i <= doc.lines; i++) {
          const line = doc.line(i);
          const { text: lineText } = line;

          // Find all {{...}} patterns in this line
          TEMPLATE_PATTERN.lastIndex = 0; // Reset regex state
          let match = TEMPLATE_PATTERN.exec(lineText);

          while (match !== null) {
            const fullMatch = match[0]; // e.g., "{{param-name}}"
            const content = match[1]; // e.g., "param-name"
            const startPos = line.from + match.index;
            const endPos = startPos + fullMatch.length;

            // Parse directive content
            const parsed = parseDirectiveContent(content);

            // Skip JSON snippets (treat as literal text)
            if (isJsonSnippet(parsed)) {
              match = TEMPLATE_PATTERN.exec(lineText);
              // eslint-disable-next-line no-continue
              continue;
            }

            // Check if it's a control flow keyword
            if (isControlFlowKeyword(parsed)) {
              builder.add(startPos, endPos, decs.controlFlow);
              match = TEMPLATE_PATTERN.exec(lineText);
              // eslint-disable-next-line no-continue
              continue;
            }

            // Otherwise, it's a parameter - check if it has a value
            const paramName = parsed;
            const paramValue = params[paramName];
            const hasValue =
              paramValue !== undefined &&
              paramValue !== null &&
              paramValue !== '';

            const decoration = hasValue
              ? decs.parameterAvailable
              : decs.parameterMissing;

            builder.add(startPos, endPos, decoration);
            match = TEMPLATE_PATTERN.exec(lineText);
          }
        }

        return builder.finish();
      }
    },
    {
      decorations: instance => instance.decorations,
    }
  );
}
